import React, { useCallback, useEffect, useState } from 'react';

import { Text } from '@mantine/core';
import CurrentPackagesTableCell from 'components/AthletePackages/CurrentPackagesTableCell';
import Table from 'components/Table';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useMissingAthletes from 'hooks/reports/athletes/useMissingAthletes';
import { useNavigate } from 'react-router-dom';
import { CellProps } from 'react-table';
import { MissingAthleteReport } from 'types/generated';

const MissingAthletesTable = ({
  setOrderingValue,
  searchFilters,
}: {
  setOrderingValue: React.Dispatch<React.SetStateAction<string>>;
  searchFilters: {
    athleteStatuses: string[];
    packages: string[];
  };
}): JSX.Element => {
  const navigate = useNavigate();
  const { dateFormat } = useLocalization();

  const [ordering, setOrdering] = useState('lastCheckInDate');
  const [ascending, setAscending] = useState(true);
  const [tableData, setTableData] = useState<MissingAthleteReport[]>([]);

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    useMissingAthletes({
      ...searchFilters,
      ordering: ascending ? ordering : `-${ordering}`,
    });

  const handleHeaderClick = (header: string): void => {
    setOrdering(header);
    setAscending(a => {
      setOrderingValue(!a ? header : `-${header}`);
      return !a;
    });
  };

  const handleNameClick = useCallback(
    (athlete: MissingAthleteReport): void => {
      navigate(`/users/athletes/${athlete.athleteId}/general-info`);
    },
    [navigate],
  );

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, setTableData]);

  const columns = [
    {
      Header: 'Name',
      accessor: 'firstName' as const,
      Cell: ({ row }: CellProps<MissingAthleteReport>) => (
        <Text
          weight='bold'
          underline
          sx={{ cursor: 'pointer', width: 'fit-content' }}
          onClick={() => handleNameClick(row.original)}
        >
          {row.original.fullName}
        </Text>
      ),
    },
    {
      Header: 'Current Status',
      accessor: 'athleteStatus' as const,
      disableSortBy: true,
    },
    {
      Header: 'Email',
      accessor: 'email' as const,
      disableSortBy: true,
    },
    {
      Header: 'Phone',
      accessor: 'phone' as const,
      disableSortBy: true,
    },
    {
      Header: 'Last Check In Date',
      accessor: 'lastCheckInDate' as const,
      Cell: ({ value }: CellProps<MissingAthleteReport>) => {
        if (value === null) {
          return 'No recorded attendance';
        }
        return dayjs(value).format(dateFormat);
      },
    },
    {
      Header: 'Upcoming Reservation',
      accessor: 'nearestNextReservedOrWaitlistedDate' as const,
      Cell: ({ value }: CellProps<MissingAthleteReport>) => {
        if (value === null) {
          return '';
        }
        return dayjs(value).format(dateFormat);
      },
    },
    {
      Header: 'Current Packages',
      accessor: 'currentPackages' as const,
      disableSortBy: true,
      Cell: ({ row }: CellProps<MissingAthleteReport>) => (
        <CurrentPackagesTableCell currentPackages={row.original.currentPackages ?? []} />
      ),
    },
  ];

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={[
          'firstName',
          'email',
          'phone',
          'lastCheckInDate',
          'nearestNextReservedOrWaitlistedDate',
        ]}
        handleHeaderClick={handleHeaderClick}
        ordering={ordering}
        ascending={ascending}
      />
    </>
  );
};

export default MissingAthletesTable;
